import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Plus,
  Minus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ImagePlus,
  Type,
  EyeOff,
  ArrowDown,
  ArrowUp,
  ArrowRight,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const HEIGHTS = [
  { value: "1/4", label: "1/4" },
  { value: "2/4", label: "2/4" },
  { value: "3/4", label: "3/4" },
  { value: "4/5", label: "4/5" },
];

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// Curated Google Fonts list (free). Family names must match Google Fonts exactly.
const GOOGLE_FONTS = [
  "Anton",
  "Archivo Black",
  "Bebas Neue",
  "Bricolage Grotesque",
  "Cormorant Garamond",
  "DM Serif Display",
  "Fraunces",
  "Inter",
  "Instrument Serif",
  "JetBrains Mono",
  "Libre Bodoni",
  "Libre Caslon Display",
  "Lora",
  "Manrope",
  "Merriweather",
  "Montserrat",
  "Oswald",
  "Outfit",
  "Playfair Display",
  "Plus Jakarta Sans",
  "Poppins",
  "Raleway",
  "Roboto",
  "Roboto Slab",
  "Source Serif 4",
  "Space Grotesk",
  "Syne",
  "Unbounded",
  "Urbanist",
  "Work Sans",
];

function heightToPct(h) {
  if (h === "1/4") return 25;
  if (h === "2/4") return 50;
  if (h === "3/4") return 75;
  return 80; // 4/5
}

function loadGoogleFontOnce(family) {
  if (!family) return;
  const id = `gfedit-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    family
  )}:wght@400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
}

const HEADER_MODES = [
  { value: "none", label: "Hidden", icon: EyeOff },
  { value: "name", label: "Form name", icon: Type },
  { value: "logo", label: "Logo", icon: ImagePlus },
];

const ALIGNS = [
  { value: "left", icon: AlignLeft, label: "Left" },
  { value: "center", icon: AlignCenter, label: "Center" },
  { value: "right", icon: AlignRight, label: "Right" },
];

export default function PageEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    slug: "",
    target_url: "",
    bg_color: "#0A0A0A",
    popup_height: "2/4",
    title: "",
    redirect_url: "https://www.gslitigation.com",
    header_mode: "none",
    header_align: "center",
    header_size: 64,
    header_color: "#FFFFFF",
    header_font: "",
    logo_url: "",
    logo_offset: 0,
    logo_offset_x: 0,
    theme_color: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/pages/${id}`);
        setForm({
          slug: data.slug,
          target_url: data.target_url,
          bg_color: data.bg_color,
          popup_height: data.popup_height,
          title: data.title || "",
          redirect_url: data.redirect_url || "https://www.gslitigation.com",
          header_mode: data.header_mode || "none",
          header_align: data.header_align || "center",
          header_size: data.header_size ?? 64,
          header_color: data.header_color || "#FFFFFF",
          header_font: data.header_font || "",
          logo_url: data.logo_url || "",
          logo_offset: data.logo_offset ?? 0,
          logo_offset_x: data.logo_offset_x ?? 0,
          theme_color: data.theme_color || "",
        });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  // Preload the selected Google font for the editor preview.
  useEffect(() => {
    if (form.header_font) loadGoogleFontOnce(form.header_font);
  }, [form.header_font]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const slugValid = useMemo(() => SLUG_RE.test(form.slug.trim().toLowerCase()), [form.slug]);
  const hexValid = useMemo(() => HEX_RE.test(form.bg_color.trim()), [form.bg_color]);
  const headerHexValid = useMemo(() => HEX_RE.test(form.header_color.trim()), [form.header_color]);
  const themeHexValid = useMemo(
    () => form.theme_color.trim() === "" || HEX_RE.test(form.theme_color.trim()),
    [form.theme_color]
  );
  const urlValid = useMemo(() => /^https?:\/\//.test(form.target_url.trim()), [form.target_url]);
  const redirectValid = useMemo(
    () => /^https?:\/\//.test(form.redirect_url.trim()),
    [form.redirect_url]
  );

  const canSave =
    slugValid && hexValid && headerHexValid && themeHexValid && urlValid && redirectValid && !saving;

  const detectThemeColor = async () => {
    if (!urlValid) {
      toast.error("Enter a valid Target URL first");
      return;
    }
    setDetecting(true);
    try {
      const { data } = await api.post("/admin/detect-theme-color", {
        url: form.target_url.trim(),
      });
      if (data.theme_color) {
        setField("theme_color", data.theme_color);
        toast.success(`Detected ${data.theme_color}`);
      } else if (data.error) {
        toast.error(data.error);
      } else {
        toast.message("No theme color found on that page", {
          description: "You can paste a hex value manually.",
        });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDetecting(false);
    }
  };

  const onUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 600 * 1024) {
      toast.error("Logo must be under 600KB. Compress or use a hosted URL.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setField("logo_url", reader.result);
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError("");
    const payload = {
      slug: form.slug.trim().toLowerCase(),
      target_url: form.target_url.trim(),
      bg_color: form.bg_color.trim(),
      popup_height: form.popup_height,
      title: form.title.trim() || null,
      redirect_url: form.redirect_url.trim(),
      header_mode: form.header_mode,
      header_align: form.header_align,
      header_size: Number(form.header_size),
      header_color: form.header_color.trim(),
      logo_url: form.logo_url ? form.logo_url : null,
      logo_offset: Number(form.logo_offset),
      logo_offset_x: Number(form.logo_offset_x),
      theme_color: form.theme_color.trim() ? form.theme_color.trim() : null,
    };
    try {
      if (isEdit) {
        await api.put(`/pages/${id}`, payload);
        toast.success("Page updated");
      } else {
        await api.post("/pages", payload);
        toast.success("Page created");
      }
      navigate("/dashboard");
    } catch (e2) {
      const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading…</div>
      </div>
    );
  }

  // size & offset step config
  const SIZE_STEP = 8; // px
  const SIZE_MIN = 16;
  const SIZE_MAX = 600;
  const OFFSET_STEP = 16;
  const OFFSET_MIN = -300;
  const OFFSET_MAX = 1000;

  const popupHPct = heightToPct(form.popup_height);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-10 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/dashboard"
            className="text-sm text-zinc-700 hover:text-zinc-900 inline-flex items-center"
            data-testid="editor-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Link>
          <span className="text-xs font-bold uppercase tracking-[0.28em] text-zinc-500">
            {isEdit ? "Edit page" : "New page"}
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1
          className="text-4xl sm:text-5xl font-black tracking-tighter text-zinc-900 leading-[0.95] mb-10"
          style={{ fontFamily: "var(--font-cabinet), Cabinet Grotesk, sans-serif" }}
          data-testid="editor-heading"
        >
          {isEdit ? "Edit wrapper" : "New wrapper"}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <form onSubmit={onSubmit} className="lg:col-span-3 space-y-8" data-testid="editor-form">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Slug
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-zinc-400 font-mono text-sm select-none">/p/</span>
                <Input
                  className="h-12 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900 font-mono"
                  placeholder="my-form"
                  value={form.slug}
                  onChange={(e) => setField("slug", e.target.value)}
                  data-testid="editor-slug-input"
                  required
                />
              </div>
              {!slugValid && form.slug.length > 0 && (
                <p className="text-xs text-red-600">
                  Use lowercase letters, numbers, and hyphens. Cannot start or end with a hyphen.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Target URL
              </Label>
              <Input
                className="h-12 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900"
                placeholder="https://forms.example.com/abc"
                value={form.target_url}
                onChange={(e) => setField("target_url", e.target.value)}
                data-testid="editor-url-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Form name
              </Label>
              <Input
                className="h-12 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900"
                placeholder="Intake form"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                data-testid="editor-title-input"
              />
              <p className="text-xs text-zinc-500">Shown on the background if you choose 'Form name'.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Background color (hex)
              </Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={
                    HEX_RE.test(form.bg_color) && form.bg_color.length === 7 ? form.bg_color : "#000000"
                  }
                  onChange={(e) => setField("bg_color", e.target.value)}
                  className="h-12 w-14 rounded-md border border-zinc-300 bg-transparent cursor-pointer"
                  data-testid="editor-color-picker"
                  aria-label="Background color picker"
                />
                <Input
                  className="h-12 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900 font-mono uppercase max-w-[180px]"
                  placeholder="#0A0A0A"
                  value={form.bg_color}
                  onChange={(e) => setField("bg_color", e.target.value)}
                  data-testid="editor-color-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Popup height
              </Label>
              <RadioGroup
                value={form.popup_height}
                onValueChange={(v) => setField("popup_height", v)}
                className="grid grid-cols-4 gap-2"
                data-testid="editor-height-radio"
              >
                {HEIGHTS.map((h) => (
                  <Label
                    key={h.value}
                    htmlFor={`h-${h.value}`}
                    className={`relative cursor-pointer rounded-md border h-12 flex items-center justify-center text-sm font-semibold transition-colors ${
                      form.popup_height === h.value
                        ? "border-zinc-900 bg-zinc-900 text-white"
                        : "border-zinc-300 text-zinc-700 hover:border-zinc-500"
                    }`}
                  >
                    <RadioGroupItem
                      value={h.value}
                      id={`h-${h.value}`}
                      className="sr-only"
                      data-testid={`editor-height-${h.value.replace("/", "-")}`}
                    />
                    {h.label}
                  </Label>
                ))}
              </RadioGroup>
            </div>

            {/* ---- Background header (form name OR logo) ---- */}
            <div className="border-t border-zinc-200 pt-8 space-y-6">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  Background header
                </Label>
                <p className="text-xs text-zinc-500 mt-1">
                  Show the form name or a logo behind the popup.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2" data-testid="editor-headermode-radio">
                {HEADER_MODES.map((m) => {
                  const Icon = m.icon;
                  const active = form.header_mode === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setField("header_mode", m.value)}
                      className={`h-14 rounded-md border flex flex-col items-center justify-center gap-1 text-xs font-semibold transition-colors ${
                        active
                          ? "border-zinc-900 bg-zinc-900 text-white"
                          : "border-zinc-300 text-zinc-700 hover:border-zinc-500"
                      }`}
                      data-testid={`editor-headermode-${m.value}`}
                    >
                      <Icon className="w-4 h-4" />
                      {m.label}
                    </button>
                  );
                })}
              </div>

              {form.header_mode !== "none" && (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                      Alignment
                    </Label>
                    <div className="grid grid-cols-3 gap-2" data-testid="editor-align-group">
                      {ALIGNS.map((a) => {
                        const Icon = a.icon;
                        const active = form.header_align === a.value;
                        return (
                          <button
                            key={a.value}
                            type="button"
                            onClick={() => setField("header_align", a.value)}
                            className={`h-11 rounded-md border inline-flex items-center justify-center gap-2 text-xs font-semibold transition-colors ${
                              active
                                ? "border-zinc-900 bg-zinc-900 text-white"
                                : "border-zinc-300 text-zinc-700 hover:border-zinc-500"
                            }`}
                            aria-label={a.label}
                            data-testid={`editor-align-${a.value}`}
                          >
                            <Icon className="w-4 h-4" />
                            {a.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                      Size
                    </Label>
                    <div className="inline-flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 w-11 rounded-md border-zinc-300"
                        onClick={() =>
                          setField(
                            "header_size",
                            Math.max(SIZE_MIN, Number(form.header_size) - SIZE_STEP)
                          )
                        }
                        data-testid="editor-size-down"
                        aria-label="Decrease size"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <div className="h-11 min-w-[88px] px-2 rounded-md border border-zinc-300 inline-flex items-center justify-center">
                        <input
                          type="number"
                          inputMode="numeric"
                          value={form.header_size}
                          min={SIZE_MIN}
                          max={SIZE_MAX}
                          onChange={(e) => setField("header_size", e.target.value)}
                          onBlur={(e) => {
                            const n = Number(e.target.value);
                            const clamped = Number.isFinite(n)
                              ? Math.max(SIZE_MIN, Math.min(SIZE_MAX, Math.round(n)))
                              : SIZE_MIN;
                            setField("header_size", clamped);
                          }}
                          style={{ fontSize: "16px" }}
                          className="w-14 text-center bg-transparent outline-none font-mono tabular-nums"
                          data-testid="editor-size-value"
                          aria-label="Header size in pixels"
                        />
                        <span className="text-sm text-zinc-500 font-mono">px</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 w-11 rounded-md border-zinc-300"
                        onClick={() =>
                          setField(
                            "header_size",
                            Math.min(SIZE_MAX, Number(form.header_size) + SIZE_STEP)
                          )
                        }
                        data-testid="editor-size-up"
                        aria-label="Increase size"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {form.header_mode === "name" && (
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                        Text color
                      </Label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={
                            HEX_RE.test(form.header_color) && form.header_color.length === 7
                              ? form.header_color
                              : "#FFFFFF"
                          }
                          onChange={(e) => setField("header_color", e.target.value)}
                          className="h-11 w-12 rounded-md border border-zinc-300 bg-transparent cursor-pointer"
                          data-testid="editor-header-color-picker"
                          aria-label="Header text color"
                        />
                        <Input
                          className="h-11 rounded-md border-zinc-300 font-mono uppercase max-w-[180px]"
                          value={form.header_color}
                          onChange={(e) => setField("header_color", e.target.value)}
                          data-testid="editor-header-color-input"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                      Font
                    </Label>
                    <Select
                      value={form.header_font || "__default__"}
                      onValueChange={(v) =>
                        setField("header_font", v === "__default__" ? "" : v)
                      }
                    >
                      <SelectTrigger
                        className="h-11 rounded-md border-zinc-300"
                        data-testid="editor-header-font-select"
                      >
                        <SelectValue placeholder="Default (Cabinet Grotesk)" />
                      </SelectTrigger>
                      <SelectContent className="max-h-80">
                        <SelectItem value="__default__">Default (Cabinet Grotesk)</SelectItem>
                        {GOOGLE_FONTS.map((f) => (
                          <SelectItem
                            key={f}
                            value={f}
                            style={{ fontFamily: `'${f}', sans-serif` }}
                            onMouseEnter={() => loadGoogleFontOnce(f)}
                          >
                            {f}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-zinc-500">
                      Loaded free from Google Fonts. Used for both name and logo header layout.
                    </p>
                  </div>

                  {form.header_mode === "logo" && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                          Logo
                        </Label>
                        <div className="flex items-center gap-3">
                          <div
                            className="h-16 w-16 rounded-md border border-zinc-300 bg-zinc-50 overflow-hidden flex items-center justify-center"
                            data-testid="editor-logo-preview"
                          >
                            {form.logo_url ? (
                              <img
                                src={form.logo_url}
                                alt="Logo"
                                className="max-h-full max-w-full object-contain"
                              />
                            ) : (
                              <ImagePlus className="w-5 h-5 text-zinc-400" />
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-11 rounded-md border-zinc-300"
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="editor-logo-upload-btn"
                          >
                            <ImagePlus className="w-4 h-4 mr-2" /> Upload
                          </Button>
                          {form.logo_url && (
                            <Button
                              type="button"
                              variant="ghost"
                              className="h-11 text-red-600"
                              onClick={() => setField("logo_url", "")}
                              data-testid="editor-logo-clear-btn"
                            >
                              Remove
                            </Button>
                          )}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={onUploadLogo}
                            data-testid="editor-logo-file-input"
                          />
                        </div>
                        <Input
                          className="h-11 rounded-md border-zinc-300 text-xs"
                          placeholder="…or paste an https:// image URL"
                          value={
                            form.logo_url && !form.logo_url.startsWith("data:")
                              ? form.logo_url
                              : ""
                          }
                          onChange={(e) => setField("logo_url", e.target.value)}
                          data-testid="editor-logo-url-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                          Slide behind popup
                        </Label>
                        <p className="text-xs text-zinc-500">
                          Push the logo down so part of it tucks behind the popup. Positive = slide
                          down.
                        </p>
                        <div className="inline-flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-11 w-11 rounded-md border-zinc-300"
                            onClick={() =>
                              setField(
                                "logo_offset",
                                Math.max(OFFSET_MIN, Number(form.logo_offset) - OFFSET_STEP)
                              )
                            }
                            data-testid="editor-offset-up"
                            aria-label="Move logo up"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <div className="h-11 min-w-[88px] px-2 rounded-md border border-zinc-300 inline-flex items-center justify-center">
                            <input
                              type="number"
                              inputMode="numeric"
                              value={form.logo_offset}
                              min={OFFSET_MIN}
                              max={OFFSET_MAX}
                              onChange={(e) => setField("logo_offset", e.target.value)}
                              onBlur={(e) => {
                                const n = Number(e.target.value);
                                const clamped = Number.isFinite(n)
                                  ? Math.max(OFFSET_MIN, Math.min(OFFSET_MAX, Math.round(n)))
                                  : 0;
                                setField("logo_offset", clamped);
                              }}
                              style={{ fontSize: "16px" }}
                              className="w-14 text-center bg-transparent outline-none font-mono tabular-nums"
                              data-testid="editor-offset-value"
                              aria-label="Logo vertical offset in pixels"
                            />
                            <span className="text-sm text-zinc-500 font-mono">px</span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-11 w-11 rounded-md border-zinc-300"
                            onClick={() =>
                              setField(
                                "logo_offset",
                                Math.min(OFFSET_MAX, Number(form.logo_offset) + OFFSET_STEP)
                              )
                            }
                            data-testid="editor-offset-down"
                            aria-label="Move logo down (behind popup)"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                          Slide left / right
                        </Label>
                        <p className="text-xs text-zinc-500">
                          Nudge the logo horizontally. Positive = right.
                        </p>
                        <div className="inline-flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-11 w-11 rounded-md border-zinc-300"
                            onClick={() =>
                              setField(
                                "logo_offset_x",
                                Math.max(-400, Number(form.logo_offset_x) - OFFSET_STEP)
                              )
                            }
                            data-testid="editor-offset-x-left"
                            aria-label="Move logo left"
                          >
                            <ArrowLeft className="w-4 h-4" />
                          </Button>
                          <div className="h-11 min-w-[88px] px-2 rounded-md border border-zinc-300 inline-flex items-center justify-center">
                            <input
                              type="number"
                              inputMode="numeric"
                              value={form.logo_offset_x}
                              min={-400}
                              max={400}
                              onChange={(e) => setField("logo_offset_x", e.target.value)}
                              onBlur={(e) => {
                                const n = Number(e.target.value);
                                const clamped = Number.isFinite(n)
                                  ? Math.max(-400, Math.min(400, Math.round(n)))
                                  : 0;
                                setField("logo_offset_x", clamped);
                              }}
                              style={{ fontSize: "16px" }}
                              className="w-14 text-center bg-transparent outline-none font-mono tabular-nums"
                              data-testid="editor-offset-x-value"
                              aria-label="Logo horizontal offset in pixels"
                            />
                            <span className="text-sm text-zinc-500 font-mono">px</span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            className="h-11 w-11 rounded-md border-zinc-300"
                            onClick={() =>
                              setField(
                                "logo_offset_x",
                                Math.min(400, Number(form.logo_offset_x) + OFFSET_STEP)
                              )
                            }
                            data-testid="editor-offset-x-right"
                            aria-label="Move logo right"
                          >
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="space-y-2 border-t border-zinc-200 pt-8">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Mobile status / footer color
              </Label>
              <p className="text-xs text-zinc-500">
                Sets the iOS / Android browser chrome to match the wrapped page (not your
                background). Tap "Detect" to read the form site's <code>theme-color</code> meta tag,
                or paste a hex value.
              </p>
              <div className="flex items-center gap-3">
                <div
                  className="h-12 w-12 rounded-md border border-zinc-300 flex-shrink-0"
                  style={{
                    backgroundColor:
                      HEX_RE.test(form.theme_color) ? form.theme_color : "transparent",
                    backgroundImage: HEX_RE.test(form.theme_color)
                      ? undefined
                      : "linear-gradient(135deg, #f4f4f5 25%, transparent 25%, transparent 50%, #f4f4f5 50%, #f4f4f5 75%, transparent 75%)",
                    backgroundSize: "12px 12px",
                  }}
                  data-testid="editor-theme-color-swatch"
                  aria-label="Current theme color"
                />
                <Input
                  className="h-12 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900 font-mono uppercase max-w-[160px]"
                  placeholder="#1A1A2E"
                  value={form.theme_color}
                  onChange={(e) => setField("theme_color", e.target.value)}
                  data-testid="editor-theme-color-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 rounded-md border-zinc-300"
                  onClick={detectThemeColor}
                  disabled={detecting || !urlValid}
                  data-testid="editor-theme-detect-btn"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  {detecting ? "Detecting…" : "Detect"}
                </Button>
                {form.theme_color && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12 text-zinc-500"
                    onClick={() => setField("theme_color", "")}
                    data-testid="editor-theme-clear-btn"
                  >
                    Clear
                  </Button>
                )}
              </div>
              {!themeHexValid && (
                <p className="text-xs text-red-600">Enter a valid hex color or leave empty.</p>
              )}
            </div>

            <div className="space-y-2 border-t border-zinc-200 pt-8">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                Redirect URL on dismiss
              </Label>
              <Input
                className="h-12 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900"
                placeholder="https://www.gslitigation.com"
                value={form.redirect_url}
                onChange={(e) => setField("redirect_url", e.target.value)}
                data-testid="editor-redirect-input"
                required
              />
            </div>

            {error && (
              <div
                className="text-sm text-red-600 border border-red-200 bg-red-50 rounded-md px-3 py-2"
                data-testid="editor-error"
              >
                {error}
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Button
                type="submit"
                disabled={!canSave}
                className="h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold tracking-wide"
                data-testid="editor-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving…" : isEdit ? "Save changes" : "Create page"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="h-12 text-zinc-700"
                data-testid="editor-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </form>

          {/* Preview */}
          <div className="lg:col-span-2">
            <div className="sticky top-24">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">
                Preview
              </div>
              <div
                className="relative w-full aspect-[9/16] rounded-xl overflow-hidden border border-zinc-200"
                style={{
                  backgroundColor: hexValid ? form.bg_color : "#000000",
                  containerType: "inline-size",
                }}
                data-testid="editor-preview"
              >
                {/* background header content */}
                {form.header_mode !== "none" && (
                  <div
                    className="absolute inset-x-0 top-0 z-0 flex items-center px-4 overflow-hidden"
                    style={{
                      height: `${100 - popupHPct}%`,
                      justifyContent:
                        form.header_align === "left"
                          ? "flex-start"
                          : form.header_align === "right"
                          ? "flex-end"
                          : "center",
                    }}
                  >
                    {form.header_mode === "name" ? (
                      <div
                        className="font-black tracking-tighter leading-none truncate"
                        style={{
                          fontFamily: form.header_font
                            ? `'${form.header_font}', 'Cabinet Grotesk', sans-serif`
                            : "var(--font-cabinet), Cabinet Grotesk, sans-serif",
                          // Match public page: header_size px on a ~400px viewport.
                          // 1cqw = 1% of preview width, so (header_size/4)cqw matches.
                          fontSize: `${Number(form.header_size) / 4}cqw`,
                          color: headerHexValid ? form.header_color : "#FFFFFF",
                          maxWidth: "94%",
                          transform: `translate(${Number(form.logo_offset_x) / 4}cqw, ${Number(form.logo_offset) / 4}cqw)`,
                        }}
                      >
                        {form.title || "Form name"}
                      </div>
                    ) : form.logo_url ? (
                      <img
                        src={form.logo_url}
                        alt=""
                        style={{
                          // Match public page: header_size px on a ~400px viewport.
                          width: `${Number(form.header_size) / 4}cqw`,
                          maxWidth: "96%",
                          transform: `translate(${Number(form.logo_offset_x) / 4}cqw, ${Number(form.logo_offset) / 4}cqw)`,
                        }}
                      />
                    ) : (
                      <div className="text-white/40 text-xs uppercase tracking-[0.2em]">
                        Upload a logo
                      </div>
                    )}
                  </div>
                )}

                <motion.div
                  key={form.popup_height}
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  transition={{ type: "spring", damping: 25, stiffness: 200 }}
                  className="absolute bottom-0 left-0 right-0 z-10 bg-white rounded-t-3xl shadow-xl"
                  style={{ height: `${popupHPct}%` }}
                >
                  <div className="flex justify-center pt-3">
                    <div className="w-10 h-1.5 rounded-full bg-zinc-300" />
                  </div>
                  <div className="px-5 py-4 text-center">
                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2">
                      /p/{form.slug || "your-slug"}
                    </div>
                    <div className="text-sm text-zinc-700 truncate">
                      {form.target_url || "https://your-form-url"}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
